import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import Reveal from "./Reveal";
import { useSite } from "@/context/SiteContext";

function CountUp({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1400;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return <span ref={ref}>{n}</span>;
}

export default function TalentPool() {
  const { content } = useSite();
  const cats = content.talent?.categories || [];

  return (
    <section
      className="section-pad relative overflow-hidden text-[#0F172A]"
      style={{ backgroundImage: "url(/talent-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
      data-testid="talent-pool-section"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundImage: "url(/talent-bg.png)", backgroundSize: "cover", backgroundPosition: "center", filter: "blur(22px)", transform: "scale(1.12)" }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-white/85" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10">
        <Reveal direction="left">
          <div className="eyebrow text-[#0096D6] mb-4">Available talent</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[46px] tracking-tight leading-[1.12]">
            Our Talent Pool, <span className="text-gradient">Ready to Work.</span>
          </h2>
          <p className="mt-4 text-[#47546E] text-base sm:text-lg max-w-2xl leading-relaxed">
            Trained specialists with 4 or more years of experience, matched to your business within 48 hours.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {cats.map((c, i) => (
            <Reveal key={c.name} direction="up" delay={i * 0.08} className="h-full">
              <div className="bg-white rounded-2xl border border-[rgba(0,180,230,0.35)] p-6 text-center h-full shadow-[0_12px_34px_rgba(13,33,84,0.07)] transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(0,163,224,0.18)]">
                <div className="font-display font-black text-4xl lg:text-[42px] text-[#00A3E0] tracking-tight" data-testid={`talent-count-${i}`}>
                  <CountUp value={c.count} />
                </div>
                <div className="mt-3 text-[13px] font-semibold text-[#47546E] leading-snug">{c.name}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
